// SelectDomainHandle.jsx
import React from 'react';
import PropTypes from 'prop-types';

// React-shared.
import { AsyncSelect } from '@exonet/react-shared/lib/Form';

// React-shared components.
import { components } from '@exonet/react-shared/lib/Form/Fields/components/AsyncSelect';

// High order component data.
import { boost } from 'app/client/utilities/hoc';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { Tooltip } from 'react-tooltip';
import i18n from 'app/client/modules/Domains/i18n';
import fetcher from './fetcher';
import store from './store';

// Styled.
import * as S from './styled';

const Option = (props) => {
  const {
    data: { company, fullName, email, phoneNumber, addressLine, value },
    isFocused,
    isSelected,
    isDisabled,
    selectProps,
  } = props;

  const anchorClass = `domain-handle-incomplete-${value || 'unknown'}`;
  const tooltipText = selectProps.t('domains:common.handleIncomplete');

  return (
    <components.MultiValueLabel {...props} isDisabled={isDisabled}>
      <S.DomainHandleOptionWrapper className={isDisabled ? anchorClass : undefined}>
        <S.DomainHandleOption $focused={isFocused} $selected={isSelected} $disabled={isDisabled}>
          <div>
            {fullName || company ? (
              <S.DomainHandlePrimary>
                {fullName ? <span>{fullName}</span> : null}
                {fullName && company ? (
                  <S.DomainHandleCompanyInline>
                    {' · '}
                    {company}
                  </S.DomainHandleCompanyInline>
                ) : null}
                {!fullName && company ? <span>{company}</span> : null}
              </S.DomainHandlePrimary>
            ) : null}

            {email || phoneNumber ? (
              <S.DomainHandleMetaRow>
                {email ? (
                  <S.DomainHandleMetaItem>
                    <S.DomainHandleMetaIcon>
                      <FontAwesomeIcon icon={byPrefixAndName.fas.envelope} />
                    </S.DomainHandleMetaIcon>
                    <S.DomainHandleMetaText title={email}>{email}</S.DomainHandleMetaText>
                  </S.DomainHandleMetaItem>
                ) : null}

                {phoneNumber ? (
                  <S.DomainHandleMetaItem>
                    <S.DomainHandleMetaIcon>
                      <FontAwesomeIcon icon={byPrefixAndName.fas.phone} />
                    </S.DomainHandleMetaIcon>
                    <S.DomainHandleMetaText title={phoneNumber}>
                      {phoneNumber}
                    </S.DomainHandleMetaText>
                  </S.DomainHandleMetaItem>
                ) : null}
              </S.DomainHandleMetaRow>
            ) : null}

            {addressLine ? (
              <S.DomainHandleMetaLine>
                <S.DomainHandleMetaIcon>
                  <FontAwesomeIcon icon={byPrefixAndName.fas['location-pin']} />
                </S.DomainHandleMetaIcon>
                <S.DomainHandleMetaText title={addressLine}>{addressLine}</S.DomainHandleMetaText>
              </S.DomainHandleMetaLine>
            ) : null}
          </div>
        </S.DomainHandleOption>

        {isDisabled ? (
          <Tooltip
            anchorSelect={`.${anchorClass}`}
            positionStrategy="fixed"
            place="top"
            border="1px solid var(--rt-border-color, transparent)"
            variant="error"
          >
            {tooltipText}
          </Tooltip>
        ) : null}
      </S.DomainHandleOptionWrapper>
    </components.MultiValueLabel>
  );
};

Option.propTypes = {
  data: PropTypes.shape({
    company: PropTypes.string,
    fullName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    addressLine: PropTypes.string,
    value: PropTypes.string,
  }),
  isFocused: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    t: PropTypes.func.isRequired,
  }),
};

const SingleValue = (props) => {
  const {
    data: { company, fullName, email },
    children,
  } = props;

  const primaryText = fullName || company || children;
  const secondaryText = fullName && company ? company : email || '';

  return (
    <components.SingleValue {...props}>
      <S.DomainHandleSingleValue>
        <S.DomainHandleSinglePrimary title={primaryText}>{primaryText}</S.DomainHandleSinglePrimary>

        {secondaryText ? (
          <S.DomainHandleSingleSecondary title={secondaryText}>
            {secondaryText}
          </S.DomainHandleSingleSecondary>
        ) : null}
      </S.DomainHandleSingleValue>
    </components.SingleValue>
  );
};

SingleValue.propTypes = {
  data: PropTypes.shape({
    company: PropTypes.string,
    fullName: PropTypes.string,
    email: PropTypes.string,
  }),
  children: PropTypes.node,
};

const SelectDomainHandle = ({
  t,
  customerId,
  defaultValue = null,
  defaultOptions = [],
  searchAction,
  fetchState: { mounting },
  ...props
}) => (
  <AsyncSelect
    {...props}
    t={t}
    loading={mounting}
    getOptions={(value) => searchAction(value, customerId)}
    defaultValue={defaultValue}
    defaultOptions={defaultOptions}
    components={{ Option, SingleValue }}
    isOptionDisabled={(option) => Boolean(option?.incomplete)}
    loadingMessage={() => t('domains:common.searchingHandles')}
    noOptionsMessage={({ inputValue }) =>
      inputValue && inputValue.trim().length > 0 ? t('domains:common.noHandlesFound') : null
    }
    showMeta={false}
  />
);

SelectDomainHandle.propTypes = {
  t: PropTypes.func.isRequired,
  customerId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ),
  ]),
  searchAction: PropTypes.func.isRequired,
  defaultOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.any,
    }),
  ),
  fetchState: PropTypes.shape({
    mounting: PropTypes.bool.isRequired,
  }).isRequired,
};

export default boost({
  i18n,
  store,
  fetcher,
  loader: false,
})(SelectDomainHandle);
